import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import QRCode from 'react-qr-code';
import { ReactComponent as DownloadIcon } from '../../../common/assets/download-icon.svg';
import styles from './staticQrModal.module.scss';

const StaticQrModal = ({
  isOpen,
  onClose,
  qrCode,
  qrUrl,
  storeId,
  storeName,
}) => {
  const qrRef = useRef();

  const downloadQRCode = () => {
    if (!qrRef.current) return;

    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const svgClone = svg.cloneNode(true);
    const svgData = new XMLSerializer().serializeToString(svgClone);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const scaleFactor = 2;
    const qrSize = 772 * scaleFactor;
    const padding = 28 * scaleFactor;
    const textHeight = 80 * scaleFactor;
    canvas.width = qrSize + padding * 2;
    canvas.height = qrSize + padding + textHeight + 50 * scaleFactor;

    ctx.scale(scaleFactor, scaleFactor);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.textRenderingOptimization = 'optimizeQuality';
    ctx.antialias = 'default';

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const img = new Image();
    const svgBlob = new Blob([svgData], {
      type: 'image/svg+xml;charset=utf-8',
    });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(
        img,
        padding / scaleFactor,
        padding / scaleFactor,
        qrSize / scaleFactor,
        qrSize / scaleFactor
      );

      if (qrCode) {
        ctx.fillStyle = 'black';
        ctx.font =
          '500 70px Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const textY = qrSize / scaleFactor + padding / scaleFactor + 90;
        ctx.fillText(qrCode, canvas.width / (2 * scaleFactor), textY);
      }

      canvas.toBlob(
        blob => {
          const downloadUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          const sanitizedStoreName = (storeName || '')
            .replace(/[^a-zA-Z0-9]/g, '_')
            .toLowerCase();
          link.download = `qr_${sanitizedStoreName}_${storeId || ''}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(downloadUrl);
        },
        'image/png',
        1.0
      );

      URL.revokeObjectURL(url);
    };

    img.src = url;
  };

  if (!isOpen) return null;

  return (
    <div className={styles.staticQrModal}>
      <div className={styles.modalOverlay} onClick={onClose}>
        <div
          className={styles.modalContent}
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>

          <div className={styles.modalBody}>
            <div className={styles.instructions}>
              <h2 className={styles.modalTitle}>Muestra el QR de tienda</h2>
              <p className={styles.modalSubTitle}>
                Indica el monto total de la orden.
              </p>
            </div>

            <div className={styles.qrContainer} ref={qrRef}>
              {qrUrl && (
                <QRCode value={qrUrl} size={300} data-testid='qr-code-svg' />
              )}
            </div>

            {qrCode && (
              <div>
                <p className={styles.qrCodeText}>{qrCode}</p>
              </div>
            )}
          </div>

          <div className={styles.modalFooter}>
            <button
              className={`${styles.downloadButton} ${!qrUrl ? styles.disabled : ''}`}
              onClick={downloadQRCode}
              disabled={!qrUrl}
            >
              <DownloadIcon className={styles.downloadIcon} />
              <span className={styles.downloadText}>Descargar QR</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

StaticQrModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  qrCode: PropTypes.string,
  qrUrl: PropTypes.string,
  storeId: PropTypes.string,
  storeName: PropTypes.string,
};

export default StaticQrModal;
